import {
  Component,
  Input,
  ElementRef,
  forwardRef,
  inject,
  input,
  output,
  viewChild,
  Signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { ToggleSwitchModalComponent } from './dialog/toggle-switch-modal.component';

@Component({
  selector: 'ui-toggle-switch',
  imports: [CommonModule, DialogModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleSwitchComponent),
      multi: true,
    },
  ],
  templateUrl: './toggle-switch.component.html',
})
export class ToggleSwitchComponent implements ControlValueAccessor {
  dialog = inject(Dialog);

  readonly switchChanged = output<boolean>();
  readonly label = input<string>();
  @Input() isChecked = false;
  readonly modalMessage = input<string>();

  readonly checkbox = viewChild<ElementRef<HTMLInputElement>>(
    'checkbox'
  ) as Signal<ElementRef<HTMLInputElement>>;

  private desiredState: boolean | null = null;

  // ControlValueAccessor methods
  onChange = (value: boolean) => {
    //
  };
  onTouched = () => {
    //
  };

  writeValue(value: boolean): void {
    this.isChecked = value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.checkbox().nativeElement.disabled = isDisabled;
  }

  handleToggleChange(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.onTouched();

    const modalMessage = this.modalMessage();
    if (modalMessage) {
      this.desiredState = !this.isChecked; // Set desired state before confirmation
      const dialogRef = this.dialog.open<{ confirmed?: boolean }>(
        ToggleSwitchModalComponent,
        {
          minWidth: '200px',
          minHeight: '300px',
          data: { message: modalMessage },
        }
      );

      dialogRef.closed.subscribe((result) => {
        if (result?.confirmed) {
          this.isChecked = this.desiredState as boolean;
          this.switchChanged.emit(this.isChecked);
          this.onChange(this.isChecked);
        }
        this.desiredState = null; // Reset temporary state
        this.checkbox().nativeElement.blur(); // Remove focus from the checkbox
      });
    } else {
      this.isChecked = !this.isChecked;
      this.switchChanged.emit(this.isChecked);
      this.onChange(this.isChecked);
      this.checkbox().nativeElement.blur(); // Remove focus from the checkbox
    }
  }
}
